import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorResponse } from '@app/core/models/error-response.model';
import * as BlogReactionActions from '@app/modules/blog-reaction/actions/blog-reaction-api.actions';
import * as BlogReaction from '@app/modules/blog-reaction/reducers';
import { BlogReactionService } from '@app/modules/blog-reaction/services/blog-reaction.service';
import { BuildErrorString } from '@app/shared/utilities/validation.utilities';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';

@Injectable()
export class BlogReactionEffects {
  constructor(
    private actions$: Actions,
    private blogreactionService: BlogReactionService,
    private messageService: NzMessageService,
    private translate: TranslateService,
    private router: Router,
    private store: Store<BlogReaction.State>
  ) {}

  /**
   * Get effects
   */
  loadBlogReactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.GetOverview),
      concatLatestFrom(() => [
        this.store.select(BlogReaction.selectBlogReactionOverviewPagination),
        this.store.select(BlogReaction.selectBlogReactionOverviewSorting),
        this.store.select(BlogReaction.selectBlogId),
        this.store.select(BlogReaction.selectBlogReactionOverviewFilter),
      ]),
      exhaustMap(([, pagination, sorting, blogId, filter]) =>
        this.blogreactionService
          .getAll(sorting, pagination, filter, blogId!)
          .pipe(
            map((response) => BlogReactionActions.GetOverviewSuccess(response)),
            catchError((response: { error: ErrorResponse }) =>
              of(
                BlogReactionActions.GetOverviewError({
                  response: response.error,
                })
              )
            )
          )
      )
    )
  );

  loadBlogReactionsFilterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.FilterOverview),
      map(() => BlogReactionActions.GetOverview())
    )
  );

  loadBlogReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.Get),
      exhaustMap((action) =>
        this.blogreactionService.get(action.id).pipe(
          map((response) => BlogReactionActions.GetSuccess(response)),
          catchError((response: { error: ErrorResponse }) =>
            of(BlogReactionActions.GetError({ response: response.error }))
          )
        )
      )
    )
  );

  /**
   * Create effects
   */
  createBlogReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.Create),
      exhaustMap((action) =>
        this.blogreactionService.create(action).pipe(
          map((blogreaction) =>
            BlogReactionActions.CreateSuccess(blogreaction)
          ),
          catchError((response: { error: ErrorResponse }) =>
            of(BlogReactionActions.CreateError({ response: response.error }))
          )
        )
      )
    )
  );

  createBlogReactionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BlogReactionActions.CreateSuccess),
        tap((blogreaction) =>
          this.translate
            .get('notification.add', { item: blogreaction.name })
            .subscribe((translatedMessage: string) =>
              this.messageService.success(translatedMessage)
            )
        )
      ),
    { dispatch: false }
  );

  /**
   * Update effects
   */
  updateBlogReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.Update),
      exhaustMap((action) =>
        this.blogreactionService.update(action).pipe(
          map((blogreaction) =>
            BlogReactionActions.UpdateSuccess(blogreaction)
          ),
          catchError((response: { error: ErrorResponse }) =>
            of(BlogReactionActions.UpdateError({ response: response.error }))
          )
        )
      )
    )
  );

  updateBlogReactionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BlogReactionActions.UpdateSuccess),
        tap((blogreaction) =>
          this.translate
            .get('notification.save', { item: blogreaction.name })
            .subscribe((translatedMessage: string) =>
              this.messageService.success(translatedMessage)
            )
        )
      ),
    { dispatch: false }
  );

  /**
   * Create update redirects & store clear
   */
  createUpdateBlogReactionSuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BlogReactionActions.CreateSuccess,
          BlogReactionActions.UpdateSuccess
        ),
        exhaustMap((action) =>
          this.router.navigate(['blogs', action.blogId, 'blog-reacties'])
        )
      ),
    { dispatch: false }
  );

  /**
   * Delete effects
   */
  deleteBlogReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.Delete),
      exhaustMap((action) =>
        this.blogreactionService.delete(action.id).pipe(
          map(() => BlogReactionActions.DeleteSuccess()),
          catchError((response: { error: ErrorResponse }) =>
            of(BlogReactionActions.DeleteError({ response: response.error }))
          )
        )
      )
    )
  );

  deleteBlogReactionSuccessReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.DeleteSuccess),
      map(() => BlogReactionActions.GetOverview())
    )
  );

  deleteBlogReactionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BlogReactionActions.DeleteSuccess, 
          BlogReactionActions.DeleteFromDetailPageSuccess),
        tap(() =>
          this.translate
            .get('notification.delete-success')
            .subscribe((translatedMessage: string) =>
              this.messageService.success(translatedMessage)
            )
        )
      ),
    { dispatch: false }
  );

  deleteBlogReactionFromDetailPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.DeleteFromDetailPage),
      exhaustMap((action) =>
        this.blogreactionService.delete(action.id).pipe(
          map(() => BlogReactionActions.DeleteFromDetailPageSuccess({ id: action.parentReactionId })),
          catchError((response: { error: ErrorResponse }) =>
            of(BlogReactionActions.DeleteFromDetailPageError({ response: response.error }))
          )
        )
      )
    )
  );

  deleteBlogReactionFromDetailPageSuccessReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.DeleteFromDetailPageSuccess),
      map((action) => BlogReactionActions.Get({ id: action.id }))
    )
  );

  /**
   * SetQuotation effects
   */
  setQuotation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.UpdateShowOnline),
      exhaustMap((action) =>
        this.blogreactionService.showOnline(action.id, action.showOnline).pipe(
          map((showOnline) =>
            BlogReactionActions.UpdateShowOnlineSuccess(showOnline)
          ),
          catchError((response: { error: ErrorResponse }) =>
            of(
              BlogReactionActions.UpdateShowOnlineError({
                response: response.error,
              })
            )
          )
        )
      )
    )
  );

  setQuotationSuccessReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.UpdateShowOnlineSuccess),
      map(() => BlogReactionActions.GetOverview())
    )
  );

  setQuotationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BlogReactionActions.UpdateShowOnline, BlogReactionActions.UpdateShowOnlineFromDetailPage),
        tap(() =>
          this.translate
            .get('blog-reactions.notification.save')
            .subscribe((translatedMessage: string) =>
              this.messageService.success(translatedMessage)
            )
        )
      ),
    { dispatch: false }
  );

  setQuotationFromDetailPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.UpdateShowOnlineFromDetailPage),
      exhaustMap((action) =>
        this.blogreactionService.showOnline(action.id, action.showOnline).pipe(
          map((blogReaction) =>
            BlogReactionActions.UpdateShowOnlineFromDetailPageSuccess(blogReaction)
          ),
          catchError((response: { error: ErrorResponse }) =>
            of(
              BlogReactionActions.UpdateShowOnlineFromDetailPageError({
                response: response.error,
              })
            )
          )
        )
      )
    )
  );

  setQuotationFromDetailPageSuccessReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BlogReactionActions.UpdateShowOnlineFromDetailPageSuccess),
      map((action) => BlogReactionActions.Get({ id: action.parentReactionId! }))
    )
  );


  blogReactionErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BlogReactionActions.DeleteError,
          BlogReactionActions.GetOverviewError,
          BlogReactionActions.UpdateShowOnlineError,
          BlogReactionActions.DeleteFromDetailPageError
        ),
        tap((error) =>
          this.messageService.error(
            BuildErrorString(error.response, this.translate)
          )
        )
      ),
    { dispatch: false }
  );
}
